import {
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import {
  CheckCircleOutline,
  ErrorOutline,
  WarningTwoTone,
} from "@material-ui/icons";
import clsx from "clsx";
import { RouteProps } from "../../types";
import { AssetLoad, AssetLoadStatus } from "../../types/documents/Assets";
import { MemoizedLoadDetails } from "./LoadDetails";

export interface Props extends RouteProps {
  loads: AssetLoad[];
  selectedLoad?: AssetLoad;
  onSelectLoad: (id: string) => void;
}

type Component = (props: Props) => JSX.Element;

export const LoadHistory: Component = ({
  loads,
  selectedLoad,
  onSelectLoad,
  ...routeProps
}) => {
  const classes = useStyles();

  const renderLoadButton = (load: AssetLoad) => {
    const isSelectedClass =
      selectedLoad?.id === load.id ? classes.selectedLoad : undefined;
    return (
      <span
        onClick={() => onSelectLoad(load.id)}
        key={load.id}
        className={clsx(classes.loadHistoryListItem, isSelectedClass)}
      >
        <span className={classes.loadStatusIcon}>
          {iconForLoadStatus(load.status)}
        </span>
        {load.clientName} - {load.fileName}
      </span>
    );
  };

  return (
    <>
      <Grid
        className={classes.header}
        container
        direction="row"
        justifyContent="flex-start"
      >
        <Grid item>
          <Typography variant="h6" align="left" className={classes.headerCrumb}>
            Executions
          </Typography>
        </Grid>
      </Grid>
      <div className={classes.loadHistory}>
        <div className={classes.loadHistoryList}>
          {loads.map(renderLoadButton)}
        </div>
        <div className={classes.loadHistoryDetails}>
          {selectedLoad !== undefined && (
            <MemoizedLoadDetails
              key={selectedLoad.id}
              {...routeProps}
              load={selectedLoad}
            />
          )}
        </div>
      </div>
    </>
  );
};

const iconForLoadStatus = (loadStatus: AssetLoadStatus): JSX.Element => {
  switch (loadStatus) {
    case AssetLoadStatus.SplittingInput:
    case AssetLoadStatus.Processing:
      return <CircularProgress variant="indeterminate" size={20} />;
    case AssetLoadStatus.AwaitingColumnSelection:
    case AssetLoadStatus.AwaitingTypeMapSelection:
      return <WarningTwoTone htmlColor="#FF9A00" />;
    case AssetLoadStatus.Succeeded:
      return <CheckCircleOutline htmlColor="#99CB01" />;
    case AssetLoadStatus.Failed:
      return <ErrorOutline color="error" />;
  }
};

const useStyles = makeStyles((theme) => ({
  header: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
    borderBottom: theme.spacing(0.125, "solid", `${theme.border.primary}`),
  },
  headerCrumb: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: theme.spacing(2.3),
  },
  loadHistory: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  loadHistoryList: {
    borderRight: theme.spacing(0.125, "solid", `${theme.border.primary}`),
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#FAFAFA",
  },
  loadHistoryListItem: {
    display: "flex",
    flexDirection: "row",
    paddingLeft: "20px",
    paddingRight: "20px",
    paddingTop: "10px",
    paddingBottom: "10px",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  selectedLoad: {
    backgroundColor: "#EEEEEE",
  },
  loadStatusIcon: {
    marginRight: "6px",
    display: "flex",
  },
  loadHistoryDetails: {
    borderLeft: 0,
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));
